/**
 * The firm's skills as the Skills tab sees them (every status), with the
 * mutations the tab offers. `refresh` is also what the builder chat calls
 * when a turn returns `invalidate: ["firm_skills"]`.
 */
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";

import type { FirmSkill, FirmSkillStatus, SkillEdits } from "../types";

export type FirmSkillsState = {
  skills: FirmSkill[];
  active: FirmSkill[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Swap one skill in place after a mutation returned the fresh row. */
  replaceSkill: (skill: FirmSkill) => void;
  removeSkill: (skillId: string) => void;
  update: (skillId: string, edits: SkillEdits) => Promise<FirmSkill>;
  setStatus: (
    skillId: string,
    status: Exclude<FirmSkillStatus, "draft">
  ) => Promise<FirmSkill>;
  /** Resolves to the skill as it was, for an Undo. */
  remove: (skillId: string) => Promise<FirmSkill | null>;
  /** Undo a delete: the skill comes back into the list. */
  restore: (skillId: string) => Promise<FirmSkill>;
};

const byTitle = (a: FirmSkill, b: FirmSkill) =>
  (a.title || a.slug).localeCompare(b.title || b.slug);

export const useFirmSkills = (scope: FirmScope): FirmSkillsState => {
  const [skills, setSkills] = useState<FirmSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const scopeKey = scope.lawFirmId ?? "";
  const apiScope = useMemo<FirmScope>(
    () => (scopeKey ? { lawFirmId: scopeKey } : {}),
    [scopeKey]
  );

  const refresh = useCallback(async () => {
    try {
      const rows = await firmKnowledgeApi.listSkills(apiScope, "all");
      if (!alive.current) return;
      setSkills([...rows].sort(byTitle));
      setError(null);
    } catch (e) {
      Sentry.captureException(e);
      if (!alive.current) return;
      setError(e instanceof Error ? e.message : "Could not load automations.");
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [apiScope]);

  useEffect(() => {
    alive.current = true;
    setLoading(true);
    void refresh();
    return () => {
      alive.current = false;
    };
  }, [refresh]);

  const replaceSkill = useCallback((skill: FirmSkill) => {
    setSkills((prev) => {
      const next = prev.some((s) => s.id === skill.id)
        ? prev.map((s) => (s.id === skill.id ? skill : s))
        : [...prev, skill];
      return next.sort(byTitle);
    });
  }, []);

  const removeSkill = useCallback((skillId: string) => {
    setSkills((prev) => prev.filter((s) => s.id !== skillId));
  }, []);

  const update = useCallback(
    async (skillId: string, edits: SkillEdits) => {
      const skill = await firmKnowledgeApi.updateSkill(
        skillId,
        edits,
        apiScope
      );
      replaceSkill(skill);
      return skill;
    },
    [apiScope, replaceSkill]
  );

  const setStatus = useCallback(
    async (skillId: string, status: Exclude<FirmSkillStatus, "draft">) => {
      const skill = await firmKnowledgeApi.setSkillStatus(
        skillId,
        status,
        apiScope
      );
      replaceSkill(skill);
      return skill;
    },
    [apiScope, replaceSkill]
  );

  const latest = useRef(skills);
  latest.current = skills;

  const remove = useCallback(
    async (skillId: string) => {
      const before = latest.current.find((s) => s.id === skillId) ?? null;
      await firmKnowledgeApi.deleteSkill(skillId, apiScope);
      removeSkill(skillId);
      return before;
    },
    [apiScope, removeSkill]
  );

  const restore = useCallback(
    async (skillId: string) => {
      const skill = await firmKnowledgeApi.restoreSkill(skillId, apiScope);
      replaceSkill(skill);
      return skill;
    },
    [apiScope, replaceSkill]
  );

  const active = useMemo(
    () => skills.filter((s) => s.status === "active"),
    [skills]
  );

  return {
    skills,
    active,
    loading,
    error,
    refresh,
    replaceSkill,
    removeSkill,
    update,
    setStatus,
    remove,
    restore
  };
};
